import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import {
  ArrowLeft, CheckCircle2, XCircle, Loader2, Clock,
  ChevronDown, ChevronRight, RefreshCw, Play,
} from 'lucide-react';
import { api } from '../api.js';
import { fmtDate, fmtDuration, statusBadge } from '../constants.js';
import LogsPanel from '../components/LogsPanel.jsx';

// ─── Run row ─────────────────────────────────────────────────────────────────
function RunRow({ run, workflow, onExpand, expanded }) {
  const StatusIcon =
    run.status === 'success' ? CheckCircle2 :
    run.status === 'error'   ? XCircle      : Loader2;

  const statusColor =
    run.status === 'success' ? 'var(--green)' :
    run.status === 'error'   ? 'var(--red)'   : 'var(--cyan)';

  return (
    <>
      <tr
        onClick={() => onExpand(run.id)}
        style={{ cursor: 'pointer', borderBottom: '1px solid var(--border)' }}
        className="hover:bg-[#141820] transition-colors"
      >
        <td style={{ padding: '12px 16px', width: 32 }}>
          {expanded
            ? <ChevronDown size={14} style={{ color: 'var(--text-muted)' }} />
            : <ChevronRight size={14} style={{ color: 'var(--text-muted)' }} />
          }
        </td>

        <td style={{ padding: '12px 0' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <StatusIcon
              size={14}
              style={{ color: statusColor, flexShrink: 0 }}
              className={run.status === 'running' ? 'spin' : ''}
            />
            <span className={`badge ${statusBadge(run.status)}`}>
              {run.status}
            </span>
          </div>
        </td>

        <td style={{ padding: '12px 16px' }}>
          <code style={{ fontFamily: 'IBM Plex Mono', fontSize: '.75rem', color: 'var(--text-dim)' }}>
            {run.id.slice(0, 8)}…
          </code>
        </td>

        <td style={{ padding: '12px 16px', color: 'var(--text-muted)', fontSize: '.82rem' }}>
          {fmtDate(run.started_at)}
        </td>

        <td style={{ padding: '12px 16px' }}>
          <span style={{ fontFamily: 'IBM Plex Mono', fontSize: '.82rem', color: 'var(--text-dim)' }}>
            {fmtDuration(run.duration_ms)}
          </span>
        </td>

        <td style={{ padding: '12px 16px', color: 'var(--text-muted)', fontSize: '.82rem', maxWidth: 300 }}>
          {run.error && (
            <span style={{ color: 'var(--red)', fontSize: '.78rem' }}>
              {run.error}
            </span>
          )}
        </td>
      </tr>

      {/* Expanded logs row */}
      {expanded && (
        <tr>
          <td colSpan={6} style={{ padding: 0 }}>
            <ExpandedRun runId={run.id} steps={workflow?.steps ?? []} />
          </td>
        </tr>
      )}
    </>
  );
}

// ─── Expanded run (lazy loads detail + logs) ──────────────────────────────────
function ExpandedRun({ runId, steps }) {
  const [run,     setRun]     = useState(null);
  const [logs,    setLogs]    = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        const [runData, logsData] = await Promise.all([
          api.runs.get(runId),
          api.runs.logs(runId),
        ]);
        setRun(runData.run);
        setLogs(logsData.logs);
      } finally {
        setLoading(false);
      }
    })();
  }, [runId]);

  if (loading) return (
    <div style={{ padding: '24px', display: 'flex', alignItems: 'center', gap: 8, color: 'var(--text-muted)' }}>
      <Loader2 size={14} className="spin" /> Loading run details…
    </div>
  );

  return (
    <div style={{ height: 320, borderBottom: '2px solid var(--border)', background: '#0d1117' }}>
      <LogsPanel run={run} logs={logs} steps={steps} />
    </div>
  );
}

// ─── Main page ────────────────────────────────────────────────────────────────
export default function WorkflowRuns() {
  const { id } = useParams();

  const [workflow,    setWorkflow]    = useState(null);
  const [runs,        setRuns]        = useState([]);
  const [total,       setTotal]       = useState(0);
  const [loading,     setLoading]     = useState(true);
  const [refreshing,  setRefreshing]  = useState(false);
  const [error,       setError]       = useState(null);
  const [expandedId,  setExpandedId]  = useState(null);
  const [triggering,  setTriggering]  = useState(false);
  const [page,        setPage]        = useState(0);

  const LIMIT = 20;

  async function loadWorkflow() {
    const data = await api.workflows.get(id);
    setWorkflow(data.workflow);
  }

  async function loadRuns(p = 0) {
    const data = await api.runs.list(id, { limit: LIMIT, page: p });
    setRuns(data.runs);
    setTotal(data.total);
    setPage(p);
  }

  async function load(showRefresh = false) {
    try {
      if (showRefresh) setRefreshing(true);
      else setLoading(true);
      await Promise.all([loadWorkflow(), loadRuns(0)]);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }

  useEffect(() => { load(); }, [id]);

  async function manualTrigger() {
    try {
      setTriggering(true);
      const result = await api.workflows.trigger(id);
      // Wait briefly then refresh
      await new Promise(r => setTimeout(r, 1200));
      await loadRuns(0);
      setExpandedId(result.runId);
    } catch (e) {
      setError(e.message);
    } finally {
      setTriggering(false);
    }
  }

  function toggleExpand(runId) {
    setExpandedId(prev => prev === runId ? null : runId);
  }

  const totalPages = Math.ceil(total / LIMIT);

  return (
    <div className="h-full overflow-y-auto" style={{ background: 'var(--bg)' }}>
      <div className="max-w-5xl mx-auto px-6 py-8">

        {/* Header */}
        <div className="flex items-center gap-4 mb-6">
          <Link to={`/workflows/${id}`} style={{ color: 'var(--text-muted)', display: 'flex', alignItems: 'center', gap: 6, textDecoration: 'none', fontSize: '.85rem' }} className="hover:text-white transition-colors">
            <ArrowLeft size={15} /> Editor
          </Link>

          <div style={{ width: 1, height: 20, background: 'var(--border)' }} />

          <div>
            <h1 style={{ fontFamily: 'IBM Plex Mono', fontSize: '1.2rem', fontWeight: 600, color: 'var(--text)', margin: 0 }}>
              {workflow?.name ?? '…'}
            </h1>
            <p style={{ color: 'var(--text-muted)', fontSize: '.8rem', marginTop: 2 }}>
              {total} run{total !== 1 ? 's' : ''}
            </p>
          </div>

          <div style={{ flex: 1 }} />

          <button
            className="btn-ghost"
            onClick={() => load(true)}
            disabled={refreshing}
            style={{ fontSize: '.82rem' }}
          >
            <RefreshCw size={13} className={refreshing ? 'spin' : ''} />
            {refreshing ? 'Refreshing…' : 'Refresh'}
          </button>

          {workflow?.trigger_type === 'manual' && (
            <button
              className="btn-primary"
              onClick={manualTrigger}
              disabled={triggering}
            >
              {triggering
                ? <><Loader2 size={13} className="spin" /> Running…</>
                : <><Play size={13} /> Run Now</>
              }
            </button>
          )}
        </div>

        {/* Error */}
        {error && (
          <div style={{ background: 'rgba(239,68,68,.1)', border: '1px solid rgba(239,68,68,.3)', borderRadius: 10, padding: '14px 18px', color: 'var(--red)', marginBottom: 16, fontSize: '.875rem' }}>
            {error}
          </div>
        )}

        {/* Loading */}
        {loading && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: 'var(--text-muted)' }}>
            <Loader2 size={16} className="spin" /> Loading runs…
          </div>
        )}

        {/* Empty state */}
        {!loading && runs.length === 0 && (
          <div style={{ border: '2px dashed var(--border)', borderRadius: 14, padding: '48px 32px', textAlign: 'center' }}>
            <Clock size={36} style={{ color: 'var(--text-muted)', margin: '0 auto 14px' }} />
            <h3 style={{ fontFamily: 'IBM Plex Mono', fontSize: '1rem', color: 'var(--text)', margin: '0 0 8px' }}>
              No runs yet
            </h3>
            <p style={{ color: 'var(--text-muted)', fontSize: '.85rem' }}>
              Trigger this workflow via webhook, schedule, or manually to see runs here.
            </p>
          </div>
        )}

        {/* Runs table */}
        {!loading && runs.length > 0 && (
          <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 12, overflow: 'hidden' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ borderBottom: '1px solid var(--border)' }}>
                  {['', 'Status', 'Run ID', 'Started', 'Duration', 'Error'].map(h => (
                    <th key={h} style={{ padding: '10px 16px', textAlign: 'left', fontSize: '.7rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '.05em', color: 'var(--text-muted)', background: 'var(--surface-2)' }}>
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {runs.map(run => (
                  <RunRow
                    key={run.id}
                    run={run}
                    workflow={workflow}
                    expanded={expandedId === run.id}
                    onExpand={toggleExpand}
                  />
                ))}
              </tbody>
            </table>

            {/* Pagination */}
            {totalPages > 1 && (
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 20px', borderTop: '1px solid var(--border)', background: 'var(--surface-2)' }}>
                <span style={{ fontSize: '.8rem', color: 'var(--text-muted)' }}>
                  Page {page + 1} of {totalPages} · {total} total
                </span>
                <div style={{ display: 'flex', gap: 8 }}>
                  <button
                    className="btn-ghost"
                    disabled={page === 0}
                    onClick={() => loadRuns(page - 1)}
                    style={{ fontSize: '.8rem', padding: '5px 12px' }}
                  >
                    Previous
                  </button>
                  <button
                    className="btn-ghost"
                    disabled={page >= totalPages - 1}
                    onClick={() => loadRuns(page + 1)}
                    style={{ fontSize: '.8rem', padding: '5px 12px' }}
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
